import type { CSSProperties, FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { FlowLines } from '@/components/generals/flow-lines'

interface PageHeroProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    actions?: ReactNode
    aside?: ReactNode
    children?: ReactNode
    align?: 'left' | 'center'
    titleSize?: 'xl' | 'lg'
    className?: string
}

const delay = (ms: number): CSSProperties => ({ '--rise-delay': `${ms}ms` } as CSSProperties)

/**
 * Cream page header shared by every inner page. Uses CSS entrance
 * animations (not scroll reveals) because it is always above the fold.
 */
export const PageHero: FC<PageHeroProps> = ({
    eyebrow,
    title,
    description,
    actions,
    aside,
    children,
    align = 'left',
    titleSize = 'xl',
    className
}): ReactNode => {
    const centered = align === 'center'

    return (
        <section className={cn('relative overflow-hidden bg-cream', className)}>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 opacity-70'>
                <FlowLines variant='waves' />
                <div className='absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-cream' />
            </div>

            <Container className='relative pb-20 pt-36 md:pb-28 md:pt-44'>
                <div className={cn('grid gap-14 lg:gap-10', aside && 'lg:grid-cols-12 lg:items-center')}>
                    <div className={cn(aside ? 'lg:col-span-6' : centered ? 'mx-auto max-w-4xl text-center' : 'max-w-5xl')}>
                        <div className='animate-rise' style={delay(40)}>
                            <Eyebrow>{eyebrow}</Eyebrow>
                        </div>

                        <h1
                            className={cn('animate-rise mt-7 font-heading font-medium text-burgundy', titleSize === 'lg' ? 'text-display-lg' : 'text-display-xl')}
                            style={delay(120)}
                        >
                            {title}
                        </h1>

                        {description && (
                            <div
                                className={cn('animate-rise mt-7 max-w-2xl text-lg leading-relaxed text-stone-dark md:text-xl', centered && 'mx-auto')}
                                style={delay(240)}
                            >
                                {description}
                            </div>
                        )}

                        {actions && (
                            <div
                                className={cn('animate-rise mt-10 flex flex-wrap items-center gap-3', centered && 'justify-center')}
                                style={delay(340)}
                            >
                                {actions}
                            </div>
                        )}
                    </div>

                    {aside && (
                        <div className='animate-rise lg:col-span-6' style={delay(300)}>
                            {aside}
                        </div>
                    )}
                </div>

                {children && (
                    <div className='animate-rise mt-16' style={delay(420)}>
                        {children}
                    </div>
                )}
            </Container>
        </section>
    )
}
